// French Tax System Data for SCD
// TVA, IS, CFE, CVAE, Taxe à l'essieu, International

// ===== TVA (Taxe sur la Valeur Ajoutée) =====
export const TVA_RATES = {
  standard: { rate: 0.20, label: 'Taux normal (20%)', applies: 'Transport de marchandises, maintenance, carburant' },
  intermediate: { rate: 0.10, label: 'Taux intermédiaire (10%)', applies: 'Transport de voyageurs (non utilisé actuellement)' },
  reduced: { rate: 0.055, label: 'Taux réduit (5,5%)', applies: 'Certains produits de première nécessité' },
};

// Which expenses are TVA-deductible and at what rate
export const TVA_DEDUCTIBLE_CATEGORIES = {
  fuel: { rate: 0.20, recoverable: 0.80, label: 'Carburant (80% récupérable)' },
  maintenance: { rate: 0.20, recoverable: 1.0, label: 'Entretien / Réparations' },
  vehicle_purchase: { rate: 0.20, recoverable: 1.0, label: 'Achat de véhicules' },
  insurance: { rate: 0, recoverable: 0, label: 'Assurance (exonérée TVA)' },
  rent_depot: { rate: 0.20, recoverable: 1.0, label: 'Loyer dépôt' },
  leasing: { rate: 0.20, recoverable: 1.0, label: 'Leasing véhicules' },
  investments: { rate: 0.20, recoverable: 1.0, label: 'Investissements' },
  salaries: { rate: 0, recoverable: 0, label: 'Salaires (hors champ TVA)' },
};

// ===== IS (Impôt sur les Sociétés) =====
export const IS_RATES = {
  reduced: { rate: 0.15, maxProfit: 42500, label: 'Taux réduit PME (15%)' },
  standard: { rate: 0.25, label: 'Taux normal (25%)' },
};

// IS quarterly installment schedule (months)
export const IS_INSTALLMENT_MONTHS = [3, 6, 9, 12];

// ===== CFE (Cotisation Foncière des Entreprises) =====
export const CFE_CONFIG = {
  baseMinimum: 237, // Minimum CFE for small businesses
  rateMultiplier: 0.015, // Applied to rental value estimate
  dueMonth: 12, // December
  label: 'Cotisation Foncière des Entreprises',
};

// ===== CVAE (Cotisation sur la Valeur Ajoutée des Entreprises) =====
export const CVAE_CONFIG = {
  revenueThreshold: 500000, // Only applies if annual revenue > 500k
  rates: [
    { maxRevenue: 500000, rate: 0 },
    { maxRevenue: 3000000, rate: 0.005 },
    { maxRevenue: 10000000, rate: 0.009 },
    { maxRevenue: Infinity, rate: 0.0015 },
  ],
  dueMonth: 6, // June (first installment) and December
  label: 'Cotisation sur la Valeur Ajoutée',
};

// ===== Taxe à l'essieu (Taxe spéciale sur certains véhicules routiers) =====
export const TAXE_ESSIEU = {
  thresholdTons: 3.5, // Only vehicles > 3.5T
  rates: [
    { maxTons: 6, annual: 124, label: '3,5T - 6T' },
    { maxTons: 11, annual: 248, label: '6T - 11T' },
    { maxTons: 18, annual: 460, label: '11T - 18T' },
    { maxTons: 26, annual: 684, label: '18T - 26T' },
    { maxTons: 40, annual: 972, label: '26T - 40T' },
    { maxTons: Infinity, annual: 1224, label: '+ de 40T' },
  ],
  dueMonths: [1, 7], // Semi-annual: January & July
  label: 'Taxe à l\'essieu',
};

// ===== Tax Regimes =====
export const TAX_REGIMES = {
  reel_simplifie: {
    id: 'reel_simplifie',
    name: 'Régime réel simplifié',
    description: 'Déclaration TVA trimestrielle. Comptabilité allégée. Idéal pour les petites structures.',
    tvaFrequency: 'quarterly', // Every 3 months
    maxRevenue: 840000, // Threshold 2024
    advantages: [
      'Déclaration TVA trimestrielle (moins de paperasse)',
      'Comptabilité simplifiée',
      'Acomptes IS simplifiés',
    ],
    disadvantages: [
      'Moins de déductions possibles',
      'Plafond de chiffre d\'affaires',
    ],
    auditRisk: 0.03, // 3% monthly chance of audit trigger
  },
  reel_normal: {
    id: 'reel_normal',
    name: 'Régime réel normal',
    description: 'Déclaration TVA mensuelle. Comptabilité complète. Pour les entreprises en croissance.',
    tvaFrequency: 'monthly',
    maxRevenue: Infinity,
    advantages: [
      'Toutes déductions fiscales disponibles',
      'Crédit de TVA plus rapide',
      'Amortissement accéléré possible',
      'Provisions déductibles',
    ],
    disadvantages: [
      'Déclaration mensuelle obligatoire',
      'Comptabilité plus lourde (coût +150 €/mois)',
      'Contrôles plus fréquents',
    ],
    auditRisk: 0.05,
    accountingCost: 150, // Monthly additional cost
  },
};

// ── 13/06/2026 — Options TVA (réglementation française) ──────────────────
// Rythme de déclaration effectif : au réel normal, l'administration tolère
// un rythme trimestriel tant que la TVA exigible annuelle reste sous
// 4 000 € (CGI, annexe II, art. 39 bis). Le régime simplifié reste
// trimestriel d'office.
export const TVA_QUARTERLY_OPTION_THRESHOLD = 4000;

export const getEffectiveTvaFrequency = (regimeId, quarterlyOption = false) => {
  const freq = TAX_REGIMES[regimeId]?.tvaFrequency || 'quarterly';
  if (freq === 'monthly' && quarterlyOption) return 'quarterly';
  return freq;
};

// Exigibilité de la TVA pour les prestations de services (transport) :
//   - 'encaissement' (défaut légal) : la TVA n'est due qu'une fois le client
//     encaissé → la déclaration porte sur la période précédente (décalage
//     d'un cycle ≈ paiements à 30 jours). Trésorerie soulagée.
//   - 'debits' (option, CGI art. 269-2-c) : exigible dès la facturation →
//     comptabilité simplifiée, le client B2B déduit plus tôt (argument
//     commercial), mais vous avancez la TVA.
export const TVA_PAYMENT_BASES = {
  encaissement: {
    id: 'encaissement',
    name: "TVA à l'encaissement",
    badge: 'Défaut légal (services)',
    description: "La TVA n'est exigible qu'à l'encaissement du prix. Votre déclaration porte sur les encaissements de la période précédente — un cycle de trésorerie de répit.",
    pro: 'Trésorerie soulagée : vous déclarez la TVA collectée avec un cycle de retard.',
    con: 'Suivi des encaissements plus rigoureux en comptabilité.',
  },
  debits: {
    id: 'debits',
    name: 'TVA sur les débits',
    badge: 'Sur option (CGI art. 269-2-c)',
    description: 'La TVA devient exigible dès l\'inscription au débit du compte client (facturation). Mention « TVA acquittée sur les débits » obligatoire sur vos factures.',
    pro: 'Comptabilité simplifiée + vos clients B2B déduisent leur TVA dès la facture (argument commercial).',
    con: "Vous avancez la TVA au Trésor avant d'être payé.",
  },
};

// ===== Tax Optimization Options =====
export const TAX_OPTIMIZATIONS = {
  amortissement_accelere: {
    id: 'amortissement_accelere',
    name: 'Amortissement accéléré',
    description: 'Amortissez vos véhicules sur 3 ans au lieu de 5. La dotation augmente de ~67 %, réduisant d\'autant le bénéfice imposable et donc l\'IS dû.',
    effect: 'Dotation × 5/3 sur la flotte → bénéfice taxable réduit',
    drawback: 'Capitaux propres diminués plus vite (valeur nette flotte réduite). Pas de retour en arrière avant fin de l\'amortissement. En cas de revente précoce d\'un véhicule, plus-value imposable plus élevée.',
    // Pas de `isReduction` : l'effet passe désormais 100 % par la dotation
    // additionnelle calculée dans `utils/depreciation.js`. Cela évite le
    // double-comptage qui existait avant (réduction d'IS appliquée + dotation
    // augmentée → l'apprenant gagnait 2× l'avantage).
    requiresRegime: 'reel_normal',
    cost: 0,
  },
  provision_entretien: {
    id: 'provision_entretien',
    name: 'Provision pour entretien',
    description: 'Provisionnez les futurs coûts d\'entretien pour réduire l\'impôt.',
    effect: 'Réduit le bénéfice imposable de 5%',
    drawback: 'Vous devez justifier ces provisions auprès du fisc (risque de redressement si non-utilisées). Bilan affiché plus prudent — peut nuire à l\'image auprès des banques pour de futurs crédits.',
    isReduction: 0.05,
    requiresRegime: null,
    cost: 0,
  },
  credit_impot_formation: {
    id: 'credit_impot_formation',
    name: 'Crédit d\'impôt formation',
    description: 'Formation des chauffeurs = crédit d\'impôt.',
    effect: 'Crédit de 500 € par chauffeur formé dans l\'année',
    drawback: 'Coût immédiat de la formation à supporter (~1 500 €/chauffeur). Chauffeur immobilisé pendant la formation (perte de productivité). Crédit plafonné à 5 chauffeurs/an.',
    isReduction: 0,
    creditPerDriver: 500,
    requiresRegime: null,
    cost: 0,
  },
};

// ===== International Tax (Phase 4) =====
export const INTERNATIONAL_TAX = {
  eu: {
    label: 'Union Européenne',
    tvaRule: 'autoliquidation',
    description: 'TVA autoliquidée sur les prestations intracommunautaires B2B. Pas de TVA facturée, le client la déclare.',
    customsDuty: 0,
    documentRequired: 'Numéro de TVA intracommunautaire',
    revenueBonus: 1.15, // 15% revenue premium on EU contracts
  },
  nonEu: {
    label: 'Hors UE (Export)',
    tvaRule: 'exoneration',
    description: 'Exportations exonérées de TVA. Droits de douane applicables.',
    customsDutyRate: 0.04, // 4% average customs duty
    documentRequired: 'Déclaration en douane (DAU)',
    transitTime: 2, // Extra days for customs
    revenueBonus: 1.25, // 25% revenue premium
  },
};

// ===== Penalties =====
export const TAX_PENALTIES = {
  late_tva: { rate: 0.10, label: 'Majoration retard TVA (10%)', perMonth: 0.004 },
  late_is: { rate: 0.10, label: 'Majoration retard IS (10%)', perMonth: 0.004 },
  audit_minor: { fine: 1500, reputationLoss: 3, label: 'Infraction mineure' },
  audit_major: { fine: 5000, reputationLoss: 8, label: 'Infraction majeure' },
  audit_clean: { reputationGain: 5, label: 'Contrôle sans anomalie' },
};

// ===== Tax Calendar Helper =====
export const getTaxDeadlines = (month, year, regime) => {
  const deadlines = [];
  const freq = TAX_REGIMES[regime]?.tvaFrequency || 'quarterly';

  // TVA deadlines
  if (freq === 'monthly') {
    deadlines.push({ type: 'tva', month, label: `Déclaration TVA - Mois ${month}` });
  } else if (freq === 'quarterly' && [3, 6, 9, 12].includes(month)) {
    deadlines.push({ type: 'tva', month, label: `Déclaration TVA - T${Math.ceil(month / 3)}` });
  }

  // IS installments
  if (IS_INSTALLMENT_MONTHS.includes(month)) {
    deadlines.push({ type: 'is', month, label: `Acompte IS - T${Math.ceil(month / 3)}` });
  }

  // CFE
  if (month === CFE_CONFIG.dueMonth) {
    deadlines.push({ type: 'cfe', month, label: 'CFE annuelle' });
  }

  // CVAE
  if (month === 6 || month === 12) {
    deadlines.push({ type: 'cvae', month, label: `CVAE ${month === 6 ? '1er acompte' : 'solde'}` });
  }

  // Taxe à l'essieu
  if (TAXE_ESSIEU.dueMonths.includes(month)) {
    deadlines.push({ type: 'essieu', month, label: `Taxe à l'essieu - S${month === 1 ? 1 : 2}` });
  }

  return deadlines;
};

// Calculate IS for a given annual profit
export const calculateIS = (annualProfit) => {
  if (annualProfit <= 0) return 0;
  if (annualProfit <= IS_RATES.reduced.maxProfit) {
    return Math.round(annualProfit * IS_RATES.reduced.rate);
  }
  const reducedPart = IS_RATES.reduced.maxProfit * IS_RATES.reduced.rate;
  const normalPart = (annualProfit - IS_RATES.reduced.maxProfit) * IS_RATES.standard.rate;
  return Math.round(reducedPart + normalPart);
};

// Calculate vehicle weight category for taxe à l'essieu
export const getVehicleTonnage = (tier) => {
  const tonnageMap = { 1: 3.5, 2: 7.5, 3: 12, 4: 19, 5: 26, 6: 40 };
  return tonnageMap[tier] || 3.5;
};

export const getEssieuRate = (tonnage) => {
  if (tonnage <= 3.5) return 0; // Below threshold
  const bracket = TAXE_ESSIEU.rates.find(r => tonnage <= r.maxTons);
  return bracket ? bracket.annual : 0;
};
